import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useRef } from 'react'
import { connect } from 'react-redux'
import useResetIntersection from 'components/custom-hooks/useResetIntersection'
import {
  ADDITIONAL_RELATED_SECTIONS,
  PDP_INTERACT_ELEM_EVENT_NAME,
  PDP_WISHLIST_REF_TYPE,
  RECOMMENDED_LAYER,
} from 'components/pdp/const'
import RelatedProductLayer from 'components/shared/product/product-related/related-product-layer'
import { withI18next } from 'lib/i18n/withI18next'
import {
  onRelatedProductSectionClick,
  segmentTrackPDPInteract,
} from 'lib/utils/product'
import RelatedProductSectionSkeleton from '../skeleton'
import { MAX_RELATED_PRODUCT_NUM } from './const'
import getDynamicComponentData from './utils'

const DynamicRelatedProductSection = ({
  auth,
  feedPos,
  internationalization: i18n,
  isPhone,
  product,
  sectionType,
  t,
}) => {
  const containerRef = useRef()
  const productId = product?.id_product
  const determinerProps = {
    defaultState: 0,
    newDeterminer: productId,
  }
  const { isAllowToPerformIntersectionEvent: shouldTrackEvent, isVisible } =
    useResetIntersection(determinerProps, containerRef, {
      threshold: 0.3,
    })

  const maxProductNumber =
    MAX_RELATED_PRODUCT_NUM[isPhone ? 'mobile' : 'desktop']

  const customSkeletonProps = {
    number: maxProductNumber,
    withCTA: true,
  }

  const { id, isFetched, productList, translatedTitle } =
    getDynamicComponentData(auth, maxProductNumber, productId, t, sectionType)

  const slicedProductList = useMemo(
    () =>
      sectionType !== ADDITIONAL_RELATED_SECTIONS[0]
        ? productList?.slice(0, maxProductNumber)
        : productList,
    [maxProductNumber, productList, sectionType],
  )

  useEffect(() => {
    if (shouldTrackEvent) {
      segmentTrackPDPInteract(
        product,
        '',
        PDP_INTERACT_ELEM_EVENT_NAME.impression,
        {
          feedPos,
          refType: PDP_WISHLIST_REF_TYPE[sectionType],
          relatedProductList: slicedProductList,
        },
      )
    }
  }, [feedPos, product, sectionType, shouldTrackEvent, slicedProductList])

  const isSectionLoading = isVisible && !isFetched
  const isSectionValid = isVisible && slicedProductList?.length > 0

  if (isSectionLoading) {
    return <RelatedProductSectionSkeleton {...customSkeletonProps} />
  }

  return (
    <div ref={containerRef}>
      {isSectionValid && (
        <RelatedProductLayer
          customSkeletonProps={customSkeletonProps}
          displayCTA
          elementID={id}
          onClick={() =>
            onRelatedProductSectionClick(
              i18n,
              productId,
              RECOMMENDED_LAYER[sectionType],
            )
          }
          productId={productId}
          products={slicedProductList}
          refType={PDP_WISHLIST_REF_TYPE[sectionType]}
          title={translatedTitle}
        />
      )}
    </div>
  )
}

DynamicRelatedProductSection.propTypes = {
  auth: PropTypes.shape({}).isRequired,
  feedPos: PropTypes.number.isRequired,
  isPhone: PropTypes.bool.isRequired,
  internationalization: PropTypes.shape({
    country: PropTypes.string.isRequired,
    language: PropTypes.string.isRequired,
  }).isRequired,
  product: PropTypes.shape({
    id_product: PropTypes.number,
  }).isRequired,
  sectionType: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
}

const Extended = withI18next()(DynamicRelatedProductSection)

export default connect((state) => ({
  auth: state.auth,
  isPhone: state.device.isPhone,
  internationalization: state.internationalization,
}))(Extended)
